import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5025/api';

const DEFAULT_SUPPORTED_CURRENCIES = [
  { code: 'INR', symbol: '₹', name: 'Indian Rupee', flag: '🇮🇳', decimals: 2 },
  { code: 'USD', symbol: '$', name: 'US Dollar', flag: '🇺🇸', decimals: 2 },
  { code: 'EUR', symbol: '€', name: 'Euro', flag: '🇪🇺', decimals: 2 },
  { code: 'GBP', symbol: '£', name: 'British Pound', flag: '🇬🇧', decimals: 2 },
  { code: 'CAD', symbol: 'CA$', name: 'Canadian Dollar', flag: '🇨🇦', decimals: 2 },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar', flag: '🇦🇺', decimals: 2 },
  { code: 'AED', symbol: 'AED', name: 'UAE Dirham', flag: '🇦🇪', decimals: 2 },
  { code: 'SGD', symbol: 'SG$', name: 'Singapore Dollar', flag: '🇸🇬', decimals: 2 },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen', flag: '🇯🇵', decimals: 0 },
  { code: 'SAR', symbol: 'SAR', name: 'Saudi Riyal', flag: '🇸🇦', decimals: 2 },
  { code: 'CHF', symbol: 'CHF', name: 'Swiss Franc', flag: '🇨🇭', decimals: 2 },
  { code: 'NZD', symbol: 'NZ$', name: 'New Zealand Dollar', flag: '🇳🇿', decimals: 2 },
];

const DEFAULT_FALLBACK_RATES = {
  INR: 1,
  USD: 0.0116,
  EUR: 0.0110,
  GBP: 0.0092,
  CAD: 0.0163,
  AUD: 0.0182,
  AED: 0.0425,
  SGD: 0.0157,
  JPY: 1.78,
  SAR: 0.0434,
  CHF: 0.0105,
  NZD: 0.0202,
};

// Client-side quick timezone & locale detector
function detectInitialCurrency() {
  try {
    const saved = localStorage.getItem('knightsteps_currency');
    if (saved && DEFAULT_FALLBACK_RATES[saved.toUpperCase()]) {
      return saved.toUpperCase();
    }

    const tz = Intl?.DateTimeFormat?.().resolvedOptions?.().timeZone || '';
    const lang = navigator?.language || '';

    if (tz.includes('Kolkata') || tz.includes('Calcutta') || lang.includes('en-IN') || lang.includes('hi')) {
      return 'INR';
    }
    if (tz.includes('London') || lang.includes('en-GB')) {
      return 'GBP';
    }
    if (tz.includes('Europe') || ['de', 'fr', 'es', 'it', 'nl'].some(l => lang.startsWith(l))) {
      return 'EUR';
    }
    if (tz.includes('Toronto') || tz.includes('Vancouver') || tz.includes('Montreal') || lang.includes('en-CA')) {
      return 'CAD';
    }
    if (tz.includes('Sydney') || tz.includes('Melbourne') || tz.includes('Brisbane') || tz.includes('Perth') || lang.includes('en-AU')) {
      return 'AUD';
    }
    if (tz.includes('Dubai')) {
      return 'AED';
    }
    if (tz.includes('Singapore')) {
      return 'SGD';
    }
    if (tz.includes('Tokyo') || lang.startsWith('ja')) {
      return 'JPY';
    }
    if (tz.includes('Riyadh')) {
      return 'SAR';
    }
    if (tz.includes('Zurich')) {
      return 'CHF';
    }
    if (tz.includes('Auckland')) {
      return 'NZD';
    }
    if (tz.includes('America') || lang.includes('en-US')) {
      return 'USD';
    }
  } catch (err) {
    console.warn('Currency auto-detection warning:', err);
  }

  // Default international standard
  return 'USD';
}

const CurrencyContext = createContext();

export function CurrencyProvider({ children }) {
  const [currency, setCurrencyState] = useState(() => detectInitialCurrency());
  const [rates, setRates] = useState(DEFAULT_FALLBACK_RATES);
  const [currencies, setCurrencies] = useState(DEFAULT_SUPPORTED_CURRENCIES);
  const [isLoading, setIsLoading] = useState(false);

  // Set and persist currency choice
  const setCurrency = useCallback((newCurrency) => {
    if (!newCurrency) return;
    const code = newCurrency.toUpperCase();
    setCurrencyState(code);
    try {
      localStorage.setItem('knightsteps_currency', code);
    } catch (e) {
      console.warn('Could not save currency to localStorage:', e);
    }
  }, []);

  // Fetch live exchange rates and server-side detected currency
  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);

    fetch(`${API_URL}/currency/rates`)
      .then(res => res.json())
      .then(data => {
        if (!isMounted) return;
        if (data.success) {
          if (data.rates && Object.keys(data.rates).length > 0) {
            setRates(prev => ({ ...prev, ...data.rates }));
          }
          if (data.currencies && Array.isArray(data.currencies) && data.currencies.length > 0) {
            setCurrencies(data.currencies);
          }
          // If the user hasn't explicitly set a preference in localStorage, adopt backend-detected currency
          const hasStored = Boolean(localStorage.getItem('knightsteps_currency'));
          if (!hasStored && data.detectedCurrency) {
            setCurrencyState(data.detectedCurrency.toUpperCase());
          }
        }
      })
      .catch(err => {
        console.warn('Currency rates fetch fallback:', err);
      })
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  // Lookup current currency metadata
  const currentMeta = currencies.find(c => c.code === currency) || {
    code: currency,
    symbol: currency === 'INR' ? '₹' : '$',
    decimals: currency === 'JPY' ? 0 : 2,
    name: currency,
    flag: '🌐',
  };

  /**
   * Convert an amount from INR base to current selected currency
   * @param {number} inrAmount - base price in INR
   * @param {string} [customCurrency] - optional target currency override
   * @returns {{ amount: number, symbol: string, formatted: string, currency: string }}
   */
  const convertPrice = useCallback((inrAmount, customCurrency = null) => {
    const targetCode = (customCurrency || currency || 'INR').toUpperCase();
    const meta = currencies.find(c => c.code === targetCode) || {
      code: targetCode,
      symbol: targetCode === 'INR' ? '₹' : '$',
      decimals: targetCode === 'JPY' ? 0 : 2,
    };

    if (inrAmount === 0 || inrAmount === null || inrAmount === undefined) {
      return {
        amount: 0,
        symbol: meta.symbol,
        formatted: '0',
        currency: meta.code,
      };
    }

    if (meta.code === 'INR') {
      const rounded = Math.round(inrAmount);
      return {
        amount: rounded,
        symbol: '₹',
        formatted: rounded.toLocaleString(),
        currency: 'INR',
      };
    }

    const rate = rates[meta.code] || DEFAULT_FALLBACK_RATES[meta.code] || 0.0116;
    const rawVal = inrAmount * rate;

    let finalAmount;
    if (meta.decimals === 0) {
      finalAmount = Math.round(rawVal);
    } else {
      finalAmount = Math.round(rawVal * 100) / 100;
      if (finalAmount < 0.5 && inrAmount > 0) finalAmount = 0.99;
    }

    const formatted = meta.decimals === 0 
      ? finalAmount.toLocaleString() 
      : finalAmount.toFixed(2);

    return {
      amount: finalAmount,
      symbol: meta.symbol,
      formatted,
      currency: meta.code,
    };
  }, [currency, currencies, rates]);

  /**
   * Format price directly with currency symbol, e.g. "$10.41" or "₹999" or "Free"
   */
  const formatPrice = useCallback((inrAmount, customCurrency = null) => {
    if (inrAmount === 0) return 'Free';
    const converted = convertPrice(inrAmount, customCurrency);
    return `${converted.symbol}${converted.formatted}`;
  }, [convertPrice]);

  return (
    <CurrencyContext.Provider
      value={{
        currency,
        setCurrency,
        currencySymbol: currentMeta.symbol,
        currentMeta,
        supportedCurrencies: currencies,
        rates,
        convertPrice,
        formatPrice,
        isLoading,
      }}
    >
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
}

export default CurrencyContext;
